<template>
  <div class="ordine-asporto">
    <!-- Header -->
    <div class="header">
      <a href="#" class="btn-back" @click.prevent="goBack">← {{ $t('indietro') }}</a>
      <h1>{{ $t('client_asporto') }}</h1>
    </div>

    <!-- Customer Info Form -->
    <div class="customer-form" v-if="!showMenu">
      <h2>{{ $t('i_tuoi_dati') }}</h2>
      <div class="form-group">
        <label for="nome">{{ $t('nome_asterisk') }}</label>
        <input 
          type="text" 
          id="nome" 
          v-model="customerInfo.nome" 
          :placeholder="$t('placeholder_nome')"
          required
        />
      </div>
      <div class="form-group">
        <label for="email">{{ $t('email_asterisk') }}</label>
        <input 
          type="email" 
          id="email" 
          v-model="customerInfo.email" 
          :placeholder="$t('placeholder_email')"
          required
        />
      </div>
      <div class="form-group">
        <label for="telefono">{{ $t('telefono_asterisk') }}</label>
        <input 
          type="tel" 
          id="telefono" 
          v-model="customerInfo.telefono" 
          :placeholder="$t('placeholder_telefono')"
          required
        />
      </div>
      <div class="form-group">
        <label for="dataRitiro">{{ $t('data_ritiro_asterisk') }}</label>
        <input 
          type="date" 
          id="dataRitiro" 
          v-model="customerInfo.dataRitiro" 
          :min="minDate"
          required
        />
      </div>
      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="customerInfo.isDelivery">
          <span>{{ $t('consegna_a_domicilio') || 'Consegna a domicilio' }}</span>
        </label>
      </div>

      <div v-if="customerInfo.isDelivery" class="delivery-fields">
        <div class="form-group">
          <label for="cap">{{ $t('cap_asterisk') || 'CAP *' }}</label>
          <input 
            type="text" 
            id="cap" 
            v-model="customerInfo.cap" 
            placeholder="es. 29140"
            @change="validateCap"
            required
          />
        </div>
        <div class="form-group">
          <label for="via">{{ $t('via_asterisk') || 'Via *' }}</label>
          <input 
            type="text" 
            id="via" 
            v-model="customerInfo.via" 
            placeholder="es. Via Roma"
            required
          />
        </div>
        <div class="form-group">
          <label for="numeroCivico">{{ $t('civico_asterisk') || 'N. Civico *' }}</label>
          <input 
            type="text" 
            id="numeroCivico" 
            v-model="customerInfo.numeroCivico" 
            placeholder="es. 10/A"
            required
          />
        </div>
        <div class="form-group">
          <label for="campanello">{{ $t('campanello_asterisk') || 'Nome Campanello *' }}</label>
          <input 
            type="text" 
            id="campanello" 
            v-model="customerInfo.campanello" 
            placeholder="es. Rossi"
            required
          />
        </div>
      </div>

      <div class="form-group">
        <label for="oraRitiro">{{ $t('ora_ritiro_asterisk') }}</label>
        
        <div class="time-range-info my-2" v-if="customerInfo.dataRitiro">
          <div v-if="!isDayAsportoActive" class="text-danger fw-bold small">
            ❌ L'asporto non è attivo il {{ getGiornoName(dayOfWeekOfSelectedDate) }}.
          </div>
          <div v-else-if="currentDayAsportoSettings && hasTimeRangesForSelectedDate" class="small text-muted">
            <strong>Orari disponibili per {{ getGiornoName(dayOfWeekOfSelectedDate) }}:</strong>
            <div class="mt-1 d-flex flex-wrap gap-2">
              <span v-if="currentDayAsportoSettings.asportoPranzoStart" class="badge bg-light text-dark border">
                Pranzo: {{ currentDayAsportoSettings.asportoPranzoStart }} - {{ currentDayAsportoSettings.asportoPranzoEnd }}
              </span>
              <span v-if="currentDayAsportoSettings.asportoCenaStart" class="badge bg-light text-dark border">
                Cena: {{ currentDayAsportoSettings.asportoCenaStart }} - {{ currentDayAsportoSettings.asportoCenaEnd }}
              </span>
            </div>
          </div>
        </div>

        <input 
          type="time" 
          id="oraRitiro" 
          v-model="customerInfo.oraRitiro" 
          :disabled="!isDayAsportoActive"
          required
        />
        <div v-if="customerInfo.oraRitiro && !isOraRitiroValida" class="text-danger small mt-1">
          ⚠️ L'orario selezionato non ricade nelle fasce orarie consentite per questo giorno.
        </div>
      </div>
      <button class="btn-primary" @click="proceedToMenu" :disabled="!isFormValid">
        {{ $t('continua_al_menu') }}
      </button>
    </div>

    <!-- Menu Selection -->
    <div class="menu-section" v-if="showMenu">
      <div class="menu-header">
        <h2>{{ $t('seleziona_i_tuoi_piatti') }}</h2>
        <span class="cart-badge" @click="showCart = !showCart">
          🛒 {{ $t('carrello') }} ({{ ordine.length }})
        </span>
      </div>

      <!-- Cart Overlay -->
      <div class="cart-overlay" v-if="showCart">
        <div class="cart-content">
          <h3>{{ $t('il_tuo_ordine') }}</h3>
          <div v-if="ordine.length === 0" class="cart-empty">
            {{ $t('carrello_vuoto') }}
          </div>
          <div v-else>
            <div class="cart-item" v-for="(item, idx) in ordine" :key="idx">
              <div class="cart-item-info">
                <div>
                  <span class="cart-item-name">{{ item.piatto }}</span>
                  <div class="cart-item-modifications" v-if="item.aggiunta.length || item.senza.length">
                    <small v-if="item.aggiunta.length" class="text-success">+ {{ item.aggiunta.map(a => a.descrizione || a).join(', ') }} <span v-if="item.costoAggiunta">(+{{ item.costoAggiunta.toFixed(2) }}€)</span></small>
                    <br v-if="item.aggiunta.length && item.senza.length">
                    <small v-if="item.senza.length" class="text-danger">- {{ item.senza.join(', ') }}</small>
                  </div>
                </div>
                <span class="cart-item-qty">x{{ item.quantita }}</span>
                <span class="cart-item-price">{{ (item.prezzo * item.quantita).toFixed(2) }}€</span>
              </div>
              <button class="btn-remove" @click="removeItem(idx)">✕</button>
            </div>
            <div class="cart-total">
              <strong>{{ $t('totale') }}: {{ totale.toFixed(2) }}€</strong>
            </div>
            <button class="btn-pay" @click="validateTimeAndProceed" :disabled="isProcessing">
              {{ isProcessing ? $t('elaborazione') : $t('paga_stripe') }}
            </button>
          </div>
          <button class="btn-close-cart" @click="showCart = false">{{ $t('chiudi') }}</button>
        </div>
      </div>

      <!-- Time Suggestion Modal -->
      <div class="modal-overlay" v-if="showTimeSuggestionModal">
        <div class="modal-content">
          <h3>{{ $t('orario_non_disponibile') || 'Orario non disponibile' }}</h3>
          <p class="suggestion-text">
            Il momento selezionato per il ritiro è troppo ravvicinato a causa degli attuali volumi di preparazione in cucina. 
            Scegli uno di questi orari disponibili più vicini:
          </p>
          
          <div class="time-slots-grid">
            <button 
              v-for="slot in suggestedTimeSlots" 
              :key="slot" 
              class="btn-time-slot" 
              :class="{ selected: selectedSuggestedTime === slot }"
              @click="selectedSuggestedTime = slot"
            >
              {{ slot }}
            </button>
          </div>

          <div class="modal-actions">
            <button class="btn-secondary" @click="showTimeSuggestionModal = false">{{ $t('annulla') }}</button>
            <button class="btn-primary" @click="confirmSuggestedTime" :disabled="!selectedSuggestedTime">
              {{ $t('conferma') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Variations Modal -->
      <div class="modal-overlay" v-if="showVariationModal">
        <div class="modal-content">
          <h3>{{ $t('personalizza') }} {{ currentDish.nome }}</h3>
          
          <div class="variation-section" v-if="currentDish.addProd && currentDish.addProd.length">
            <h4>{{ $t('aggiunte') }}</h4>
            <div class="variation-grid">
              <label v-for="add in currentDish.addProd" :key="add.prodottoId || add" class="variation-item">
                <input type="checkbox" :value="add" v-model="selectedAggiunte">
                <span>{{ add.descrizione || add }}</span>
              </label>
            </div>
          </div>

          <div class="variation-section" v-if="currentDish.removeProd && currentDish.removeProd.length">
            <h4>{{ $t('senza') }}</h4>
            <div class="variation-grid">
              <label v-for="rem in currentDish.removeProd" :key="rem" class="variation-item">
                <input type="checkbox" :value="rem" v-model="selectedSenza">
                <span>{{ rem }}</span>
              </label>
            </div>
          </div>

          <div class="modal-actions">
            <button class="btn-secondary" @click="showVariationModal = false">{{ $t('annulla') }}</button>
            <button class="btn-primary" @click="confirmVariations">{{ $t('conferma') }}</button>
          </div>
        </div>
      </div>

      <!-- Category Filter Bar -->
      <div class="category-filter-container">
        <div class="category-filter">
          <div 
            class="category-item" 
            :class="{ active: selectedCategory === 'all' }"
            @click="selectedCategory = 'all'"
          >
            <div class="cat-icon"><i class="fas fa-th-large"></i></div>
            <span class="cat-label">{{ $t('tutti') || 'Tutti' }}</span>
          </div>
          <div 
            v-for="cat in categories" 
            :key="cat.id"
            class="category-item" 
            :class="{ active: selectedCategory === cat.id, hidden: !hasItemsInCategory(cat.id) }"
            @click="selectedCategory = cat.id"
            v-show="hasItemsInCategory(cat.id)"
          >
            <div class="cat-icon"><i :class="cat.icon"></i></div>
            <span class="cat-label">{{ $t(cat.id) }}</span>
          </div>
        </div>
      </div>

      <!-- Menu Categories -->
      <div class="menu-categories">
        <div class="category" v-if="antipasti.length" v-show="selectedCategory === 'all' || selectedCategory === 'antipasti'">
          <h3>{{ $t('nostri_antipasti') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in antipasti" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="primi.length" v-show="selectedCategory === 'all' || selectedCategory === 'primi'">
          <h3>{{ $t('nostri_primi') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in primi" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="secondi.length" v-show="selectedCategory === 'all' || selectedCategory === 'secondi'">
          <h3>{{ $t('nostri_secondi') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in secondi" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="contorni.length" v-show="selectedCategory === 'all' || selectedCategory === 'contorni'">
          <h3>{{ $t('nostri_contorni') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in contorni" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="pizze.length" v-show="selectedCategory === 'all' || selectedCategory === 'pizza'">
          <h3>{{ $t('nostra_pizza') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in pizze" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="focacce.length" v-show="selectedCategory === 'all' || selectedCategory === 'focaccia'">
          <h3>{{ $t('nostra_focaccia') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in focacce" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="category" v-if="tranci.length" v-show="selectedCategory === 'all' || selectedCategory === 'trancio'">
          <h3>{{ $t('nostro_trancio') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in tranci" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        <div class="category" v-if="teglie.length" v-show="selectedCategory === 'all' || selectedCategory === 'teglia'">
          <h3>{{ $t('nostra_teglia') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in teglie" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="combos.length" v-show="selectedCategory === 'all' || selectedCategory === 'combo'">
          <h3>{{ $t('nostra_combo') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in combos" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="insalate.length" v-show="selectedCategory === 'all' || selectedCategory === 'insalate'">
          <h3>{{ $t('nostre_insalate') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in insalate" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="category" v-if="sushi.length" v-show="selectedCategory === 'all' || selectedCategory === 'sushi'">
          <h3>{{ $t('nostro_sushi') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in sushi" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        <div class="category" v-if="poke.length" v-show="selectedCategory === 'all' || selectedCategory === 'poke'">
          <h3>{{ $t('nostro_poke') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in poke" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="frutta.length" v-show="selectedCategory === 'all' || selectedCategory === 'frutta'">
          <h3>{{ $t('nostra_frutta') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in frutta" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="dolci.length" v-show="selectedCategory === 'all' || selectedCategory === 'dolci'">
          <h3>{{ $t('nostri_dolci') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in dolci" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="amari.length" v-show="selectedCategory === 'all' || selectedCategory === 'amari'">
          <h3>{{ $t('nostri_amari') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in amari" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="bevande.length" v-show="selectedCategory === 'all' || selectedCategory === 'bevande'">
          <h3>{{ $t('nostre_bevande') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in bevande" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="category" v-if="calice.length" v-show="selectedCategory === 'all' || selectedCategory === 'calice'">
          <h3>{{ $t('nostro_calice') }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in calice" :key="dish.nome">
              <img v-if="dish.immagine" class="dish-img" :src="dish.immagine" :alt="dish.nome" />
              <div class="dish-info">
                <h4>{{ getTranslatedName(dish) }}</h4>
                <div v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="description-container" :class="{ expanded: dish.expanded }">
                  <p>{{ getTranslatedDesc(dish) }}</p>
                  <div v-if="getAllergeni(dish)" class="allergeni-info">
                    <strong>{{ $t('allergeni') || 'Allergeni' }}:</strong> {{ getAllergeni(dish) }}
                  </div>
                </div>
                <div class="dish-footer">
                  <span class="price">{{ dish.prezzo }}€</span>
                  <div class="dish-actions">
                    <button v-if="getTranslatedDesc(dish) || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Glovo Modal -->
    <div class="modal-overlay" v-if="showGlovoModal">
      <div class="modal-content" style="text-align: center;">
        <h3>{{ $t('zona_non_coperta') || 'Zona non coperta' }}</h3>
        <p style="margin: 20px 0; font-size: 1.2rem; font-weight: bold;">{{ $t('ordina_da_glovo') || 'Ordina tramite Glovo' }}</p>
        <div class="modal-actions" style="justify-content: center;">
          <button class="btn-primary" @click="showGlovoModal = false">{{ $t('ho_capito') || 'Ho capito' }}</button>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div class="error-message" v-if="errorMessage">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'OrdineAsporto',
  data() {
    return {
      store,
      customerInfo: {
        nome: '',
        email: '',
        telefono: '',
        dataRitiro: new Date().toISOString().split('T')[0],
        oraRitiro: '',
        isDelivery: false,
        cap: '',
        via: '',
        numeroCivico: '',
        campanello: ''
      },
      showGlovoModal: false,
      showMenu: false,
      showCart: false,
      piatti: [],
      antipasti: [],
      primi: [],
      secondi: [],
      contorni: [],
      pizze: [],
      sushi: [],
      dolci: [],
      frutta: [],
      amari: [],
      bevande: [],
      calice: [],
      poke: [],
      tranci: [],
      focacce: [],
      teglie: [],
      combos: [],
      insalate: [],
      selectedCategory: 'all',
      categories: [
        { id: 'antipasti', label: 'nostri_antipasti', icon: 'fas fa-concierge-bell' },
        { id: 'primi', label: 'nostri_primi', icon: 'fas fa-utensils' },
        { id: 'secondi', label: 'nostri_secondi', icon: 'fas fa-drumstick-bite' },
        { id: 'poke', label: 'nostro_poke', icon: 'fas fa-concierge-bell' },
        { id: 'contorni', label: 'nostri_contorni', icon: 'fas fa-leaf' },
        { id: 'pizza', label: 'nostra_pizza', icon: 'fas fa-pizza-slice' },
        { id: 'focaccia', label: 'nostra_focaccia', icon: 'fas fa-bread-slice' },
        { id: 'trancio', label: 'nostro_trancio', icon: 'fas fa-pizza-slice' },
        { id: 'teglia', label: 'nostra_teglia', icon: 'fas fa-layer-group' },
        { id: 'combo', label: 'nostra_combo', icon: 'fas fa-plus-circle' },
        { id: 'insalate', label: 'nostre_insalate', icon: 'fas fa-leaf' },
        { id: 'sushi', label: 'nostro_sushi', icon: 'fas fa-fish' },
        { id: 'dolci', label: 'nostri_dolci', icon: 'fas fa-ice-cream' },
        { id: 'frutta', label: 'nostra_frutta', icon: 'fas fa-apple-alt' },
        { id: 'amari', label: 'nostri_amari', icon: 'fas fa-glass-martini-alt' },
        { id: 'bevande', label: 'nostre_bevande', icon: 'fas fa-wine-bottle' },
        { id: 'calice', label: 'nostro_calice', icon: 'fas fa-wine-glass' },

      ],
      ordine: [],
      isProcessing: false,
      errorMessage: '',
      showVariationModal: false,
      currentDish: null,
      selectedAggiunte: [],
      selectedSenza: [],
      showTimeSuggestionModal: false,
      suggestedTimeSlots: [],
      selectedSuggestedTime: null,
      asportoSettings: null
    }
  },
  computed: {
    dayOfWeekOfSelectedDate() {
      if (!this.customerInfo.dataRitiro) return null;
      const parts = this.customerInfo.dataRitiro.split('-');
      if (parts.length !== 3) return null;
      const yyyy = parseInt(parts[0], 10);
      const mm = parseInt(parts[1], 10) - 1;
      const dd = parseInt(parts[2], 10);
      const d = new Date(yyyy, mm, dd);
      return d.getDay();
    },
    currentDayAsportoSettings() {
      if (!this.asportoSettings) return null;
      const dow = this.dayOfWeekOfSelectedDate;
      if (dow !== null && dow !== undefined && this.asportoSettings.giorni) {
        const daySetting = this.asportoSettings.giorni[dow] || this.asportoSettings.giorni[String(dow)];
        if (daySetting) {
          return {
            attivo: daySetting.attivo !== false,
            asportoPranzoStart: daySetting.asportoPranzoStart || this.asportoSettings.asportoPranzoStart,
            asportoPranzoEnd: daySetting.asportoPranzoEnd || this.asportoSettings.asportoPranzoEnd,
            asportoCenaStart: daySetting.asportoCenaStart || this.asportoSettings.asportoCenaStart,
            asportoCenaEnd: daySetting.asportoCenaEnd || this.asportoSettings.asportoCenaEnd
          };
        }
      }
      return {
        attivo: true,
        asportoPranzoStart: this.asportoSettings.asportoPranzoStart,
        asportoPranzoEnd: this.asportoSettings.asportoPranzoEnd,
        asportoCenaStart: this.asportoSettings.asportoCenaStart,
        asportoCenaEnd: this.asportoSettings.asportoCenaEnd
      };
    },
    isDayAsportoActive() {
      const s = this.currentDayAsportoSettings;
      return s ? s.attivo : true;
    },
    hasTimeRangesForSelectedDate() {
      const s = this.currentDayAsportoSettings;
      if (!s || !s.attivo) return false;
      return !!(s.asportoPranzoStart || s.asportoCenaStart);
    },
    isOraRitiroValida() {
      if (!this.customerInfo.dataRitiro || !this.customerInfo.oraRitiro) return true;
      const s = this.currentDayAsportoSettings;
      if (!s) return true;
      if (!s.attivo) return false;

      const ora = this.customerInfo.oraRitiro;
      const isLunch = s.asportoPranzoStart && s.asportoPranzoEnd ?
        (ora >= s.asportoPranzoStart && ora <= s.asportoPranzoEnd) : false;
      const isDinner = s.asportoCenaStart && s.asportoCenaEnd ?
        (ora >= s.asportoCenaStart && ora <= s.asportoCenaEnd) : false;

      if (!s.asportoPranzoStart && !s.asportoPranzoEnd && !s.asportoCenaStart && !s.asportoCenaEnd) {
        return true;
      }

      return isLunch || isDinner;
    },
    isFormValid() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[\d\s+\-()]{6,20}$/;
      const basic = this.customerInfo.nome.trim() && 
             this.customerInfo.email.trim() &&
             emailRegex.test(this.customerInfo.email) &&
             this.customerInfo.telefono.trim() &&
             phoneRegex.test(this.customerInfo.telefono) &&
             this.customerInfo.dataRitiro &&
             this.customerInfo.oraRitiro &&
             this.isDayAsportoActive &&
             this.isOraRitiroValida;

      if (!this.customerInfo.isDelivery) return basic;

      const allowedCaps = ['29140', '29620', '29130'];
      const deliveryValid = this.customerInfo.cap && 
                           allowedCaps.includes(this.customerInfo.cap.trim()) &&
                           this.customerInfo.via && 
                           this.customerInfo.numeroCivico && 
                           this.customerInfo.campanello;
      
      return basic && deliveryValid;
    },
    minDate() {
      return new Date().toISOString().split('T')[0]
    },
    totale() {
      return this.ordine.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0)
    },
    totalItems() {
      return this.ordine.reduce((sum, item) => sum + item.quantita, 0)
    },
    hasTimeRanges() {
      if (!this.asportoSettings) return false
      return this.asportoSettings.asportoPranzoStart || this.asportoSettings.asportoCenaStart
    }
  },
  methods: {
    goBack() {
      if (this.showMenu) {
        this.showMenu = false
      } else {
        this.$router.push({ name: "Home"})
      }
    },
    validateCap() {
      if (!this.customerInfo.isDelivery) return;
      const allowed = ['29140', '29620', '29130'];
      const cap = this.customerInfo.cap ? this.customerInfo.cap.toString().trim() : '';
      if (cap && !allowed.includes(cap)) {
        this.showGlovoModal = true;
      }
    },
    proceedToMenu() {
      if (this.isFormValid) {
        this.showMenu = true
      }
    },
    async displayMenu() {
      try {
        const response = await this.axios.post(this.store.path + 'displayMenu')
        if (response.data.messaggio) {
          response.data.messaggio.forEach(element => {
            element.expanded = false;
            this.piatti.push(element)
          })
          this.createOrderCards()
        }
      } catch (error) {
        console.error('Error loading menu:', error)
        this.errorMessage = this.$t('products_load_failed')
      }
    },
    createOrderCards() {
      this.piatti.forEach(element => {
        if (element.portata === 'antipasti') this.antipasti.push(element)
        if (element.portata === 'primi') this.primi.push(element)
        if (element.portata === 'secondi') this.secondi.push(element)
        if (element.portata === 'contorni') this.contorni.push(element)
        if (element.portata === 'pizza') this.pizze.push(element)
        if (element.portata === 'focaccia') this.focacce.push(element)
        if (element.portata === 'trancio') this.tranci.push(element)
        if (element.portata === 'teglia') this.teglie.push(element)
        if (element.portata === 'combo') this.combos.push(element)
        if (element.portata === 'insalate') this.insalate.push(element)
        if (element.portata === 'sushi') this.sushi.push(element)
        if (element.portata === 'dolci') this.dolci.push(element)
        if (element.portata === 'frutta') this.frutta.push(element)
        if (element.portata === 'amari') this.amari.push(element)
        if (element.portata === 'bevande') this.bevande.push(element)
        if (element.portata === 'calice') this.calice.push(element)
        if (element.portata === 'poke') this.poke.push(element)

      })
    },
    getGiornoName(dow) {
      if (dow === null || dow === undefined) return '';
      const names = {
        0: 'Domenica',
        1: 'Lunedì',
        2: 'Martedì',
        3: 'Mercoledì',
        4: 'Giovedì',
        5: 'Venerdì',
        6: 'Sabato'
      };
      return names[dow] || '';
    },
    async fetchSettings() {
      try {
        const response = await this.axios.get(this.store.path + 'prenotazioni-setting')
        const setting = response.data.find(s => s.codicesala === 'asporto')
        if (setting) {
          this.asportoSettings = setting
        }
      } catch (error) {
        console.error('Error fetching asporto settings:', error)
      }
    },
    getTranslatedName(item) {
      if (!item) return '';
      const locale = this.$i18n.locale;
      if (locale === 'en') return item.nomeEn || item.nome;
      if (locale === 'es') return item.nomeEs || item.nome;
      return item.nome;
    },
    getTranslatedDesc(item) {
      if (!item) return '';
      const locale = this.$i18n.locale;
      if (locale === 'en') return item.descrizioneEn || item.descrizione;
      if (locale === 'es') return item.descrizioneEs || item.descrizione;
      return item.descrizione;
    },
    hasItemsInCategory(catId) {
      if (catId === 'antipasti') return this.antipasti.length > 0;
      if (catId === 'primi') return this.primi.length > 0;
      if (catId === 'secondi') return this.secondi.length > 0;
      if (catId === 'contorni') return this.contorni.length > 0;
      if (catId === 'pizza') return this.pizze.length > 0;
      if (catId === 'sushi') return this.sushi.length > 0;
      if (catId === 'dolci') return this.dolci.length > 0;
      if (catId === 'frutta') return this.frutta.length > 0;
      if (catId === 'amari') return this.amari.length > 0;
      if (catId === 'bevande') return this.bevande.length > 0;
      if (catId === 'calice') return this.calice.length > 0;
      if (catId === 'poke') return this.poke.length > 0;
      if (catId === 'trancio') return this.tranci.length > 0;
      if (catId === 'focaccia') return this.focacce.length > 0;
      if (catId === 'teglia') return this.teglie.length > 0;
      if (catId === 'combo') return this.combos.length > 0;
      if (catId === 'insalate') return this.insalate.length > 0;
      return false;
    },
    toggleDetails(dish) {
      dish.expanded = !dish.expanded;
    },
    getAllergeni(item) {
      if (!item) return '';
      const locale = this.$i18n.locale;
      if (locale === 'en') return item.allergeniEn || item.allergeni;
      if (locale === 'es') return item.allergeniEs || item.allergeni;
      return item.allergeni;
    },
    addToCart(dish) {
      if ((dish.addProd && dish.addProd.length) || (dish.removeProd && dish.removeProd.length)) {
        this.currentDish = dish
        this.selectedAggiunte = []
        this.selectedSenza = []
        this.showVariationModal = true
      } else {
        this.addOrderItem(dish.nome, dish.prezzo, dish.portata, [], [], dish._id || dish.id)
      }
    },
    confirmVariations() {
      let costoAggiunta = 0;
      if (this.selectedAggiunte.length && this.currentDish.addPrice && this.currentDish.addPrice.length) {
        this.selectedAggiunte.forEach(add => {
          const nomeAggiunta = add.descrizione || add;
          const priceEntry = this.currentDish.addPrice.find(p => p.nome === nomeAggiunta);
          if (priceEntry && priceEntry.prezzo) {
            costoAggiunta += Number(priceEntry.prezzo);
          }
        });
      }
      const prezzoTotale = this.currentDish.prezzo + costoAggiunta;
      this.addOrderItem(this.currentDish.nome, prezzoTotale, this.currentDish.portata, [...this.selectedAggiunte], [...this.selectedSenza], this.currentDish._id || this.currentDish.id, costoAggiunta)
      this.showVariationModal = false
    },
    addOrderItem(nome, prezzo, portata, aggiunta, senza, id, costoAggiunta = 0) {
      const existing = this.ordine.find(item => 
        item.piatto === nome && 
        item.piattoId === id &&
        JSON.stringify(item.aggiunta) === JSON.stringify(aggiunta) && 
        JSON.stringify(item.senza) === JSON.stringify(senza)
      )
      
      if (existing) {
        existing.quantita++
      } else {
        this.ordine.push({
          piatto: nome,
          piattoId: id,
          prezzo: prezzo,
          costoAggiunta: costoAggiunta,
          portata: portata,
          quantita: 1,
          aggiunta: aggiunta,
          senza: senza
        })
      }
    },
    removeItem(index) {
      this.ordine.splice(index, 1)
    },
    async validateTimeAndProceed() {
      if (this.ordine.length === 0) {
        this.errorMessage = 'Il carrello è vuoto'
        return
      }

      this.isProcessing = true
      this.errorMessage = ''

      try {
        const response = await this.axios.post(this.store.path + 'validaOrarioRitiro', {
          dataRitiro: this.customerInfo.dataRitiro,
          oraRitiro: this.customerInfo.oraRitiro,
          ordinazione: this.ordine
        });

        if (response.data.feasible) {
          // Orario copatibile, procediamo
          this.proceedToPayment();
        } else {
          // Orario non copatibile
          this.isProcessing = false;
          
          if (response.data.messaggio && (!response.data.slots || response.data.slots.length === 0)) {
            this.errorMessage = response.data.messaggio;
            return;
          }

          this.suggestedTimeSlots = response.data.slots || [];
          this.selectedSuggestedTime = null;
          // Se la data scelta era per un giorno precedente la riporta a oggi
          if (new Date(this.customerInfo.dataRitiro) < new Date(this.minDate)) {
             this.customerInfo.dataRitiro = this.minDate;
          }
          this.showTimeSuggestionModal = true;
        }
      } catch (error) {
        console.error('Validation error:', error)
        this.errorMessage = error.response?.data?.messaggio || 'Errore durante la validazione orari'
        this.isProcessing = false
      }
    },
    confirmSuggestedTime() {
      if (this.selectedSuggestedTime) {
         this.customerInfo.oraRitiro = this.selectedSuggestedTime;
         this.showTimeSuggestionModal = false;
         this.proceedToPayment();
      }
    },
    async proceedToPayment() {

      try {
        const response = await this.axios.post(this.store.path + 'creaSessionePagamento', {
          nome: this.customerInfo.nome,
          email: this.customerInfo.email,
          telefono: this.customerInfo.telefono,
          dataRitiro: this.customerInfo.dataRitiro,
          oraRitiro: this.customerInfo.oraRitiro,
          isDelivery: this.customerInfo.isDelivery,
          cap: this.customerInfo.cap,
          via: this.customerInfo.via,
          numeroCivico: this.customerInfo.numeroCivico,
          campanello: this.customerInfo.campanello,
          ordinazione: this.ordine,
          totale: Math.round(this.totale * 100) // in cents
        })

        if (response.data.sessionUrl) {
          // Redirect to Stripe Checkout
          window.location.href = response.data.sessionUrl
        } else {
          this.errorMessage = 'Errore nella creazione del pagamento'
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.errorMessage = error.response?.data?.messaggio || 'Errore durante il pagamento'
      } finally {
        this.isProcessing = false
      }
    }
  },
  beforeMount() {
    this.displayMenu()
    this.fetchSettings()
  }
}
</script>

<style scoped>
.time-range-info {
  margin-top: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  font-size: 0.85rem;
}

.time-range-info small {
  display: block;
  color: #00d9ff;
  margin-bottom: 4px;
}

.time-range-info .ranges {
  display: flex;
  gap: 15px;
  color: #aaa;
}
.ordine-asporto {
  height: 100vh;
  overflow-y: auto;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
  padding: 20px;
  padding-bottom: 120px;
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
}

.header h1 {
  font-size: 1.8rem;
  margin: 0;
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-back {
  color: #00d9ff;
  text-decoration: none;
  font-size: 1rem;
}

/* Customer Form */
.customer-form {
  max-width: 400px;
  margin: 0 auto;
  padding: 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
}

.customer-form h2 {
  margin-bottom: 20px;
  color: #00ff88;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #aaa;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: #666;
}

.form-group input:focus {
  outline: none;
  border-color: #00d9ff;
}

.btn-primary {
  width: 100%;
  padding: 14px;
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  border: none;
  border-radius: 8px;
  color: #1a1a2e;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary:not(:disabled):hover {
  transform: translateY(-2px);
}

/* Menu Section */
.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.menu-header h2 {
  color: #00ff88;
}

.cart-badge {
  background: linear-gradient(90deg, #ff6b6b, #ffa500);
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
}

/* Cart Overlay */
.cart-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.cart-content {
  background: #1a1a2e;
  padding: 30px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.cart-content h3 {
  margin-bottom: 20px;
  color: #00ff88;
}

.cart-empty {
  text-align: center;
  color: #666;
  padding: 20px;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.cart-item-info {
  display: flex;
  gap: 15px;
  align-items: center;
}

.cart-item-name {
  font-weight: 500;
}

.cart-item-qty {
  color: #00d9ff;
}

.cart-item-price {
  color: #00ff88;
}

.btn-remove {
  background: #ff4444;
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
}

.cart-total {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #00ff88;
  font-size: 1.3rem;
  text-align: right;
}

.btn-pay {
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  background: linear-gradient(90deg, #6772e5, #9b59b6);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn-pay:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-pay:not(:disabled):hover {
  transform: translateY(-2px);
}

.btn-close-cart {
  width: 100%;
  padding: 12px;
  margin-top: 10px;
  background: transparent;
  border: 1px solid #666;
  border-radius: 8px;
  color: #666;
  cursor: pointer;
}

/* Menu Categories */
.category {
  margin-bottom: 40px;
}

.category h3 {
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: #00d9ff;
  border-bottom: 2px solid #00d9ff;
  padding-bottom: 10px;
}

.dishes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.dish-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s, background 0.2s;
  display: flex;
  flex-direction: column;
}

.dish-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.1);
}

.dish-img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.dish-info {
  padding: 15px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.dish-card h4 {
  margin: 0 0 8px 0;
  color: white;
  font-size: 1.1rem;
}

.dish-card p {
  color: #aaa;
  font-size: 0.85rem;
  margin: 0;
}

.description-container {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 0.4s ease, opacity 0.3s ease;
  opacity: 0;
  margin-bottom: 0;
}

.description-container.expanded {
  max-height: 300px;
  opacity: 1;
  margin-bottom: 15px;
  margin-top: 10px;
}

.allergeni-info {
  margin-top: 10px;
  font-size: 0.85rem;
  color: #e67e21; /* Slightly different brand orange for dark mode */
  border-top: 1px solid rgba(255,255,255,0.05);
  padding-top: 8px;
}

.allergeni-info strong {
  color: #00d9ff;
}

.dish-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price {
  font-size: 1.2rem;
  font-weight: bold;
  color: #00ff88;
}

.btn-add {
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  color: #1a1a2e;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;
  flex-grow: 0;
  white-space: nowrap;
}

.dish-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-grow: 0;
  justify-content: flex-end;
}

.btn-info-toggle {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 217, 255, 0.2);
  color: #00d9ff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-info-toggle:hover {
  background: rgba(0, 217, 255, 0.1);
  border-color: #00d9ff;
  transform: scale(1.1);
}

.btn-add:hover {
  transform: scale(1.05);
}

/* Error Message */
.error-message {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4444;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 999;
}

/* Variation Modal */
.checkbox-group {
  margin: 15px 0;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;
}
.checkbox-label input {
  width: 20px;
  height: 20px;
}
.delivery-fields {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 20px;
  color: black;
}
.delivery-fields label {
  color: black;
}
.delivery-fields input {
  color: black;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: #1a1a2e;
  padding: 30px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(0, 217, 255, 0.3);
}

.modal-content h3 {
  color: #00ff88;
  margin-top: 0;
  margin-bottom: 20px;
}

.variation-section {
  margin-bottom: 25px;
}

.variation-section h4 {
  color: #00d9ff;
  border-bottom: 1px solid rgba(0, 217, 255, 0.2);
  padding-bottom: 8px;
  margin-bottom: 15px;
}

.variation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.variation-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  transition: background 0.2s;
}

.variation-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.variation-item input {
  width: 18px;
  height: 18px;
  accent-color: #00ff88;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

/* Category Filter Bar Styles */
.category-filter-container {
  position: sticky;
  top: -20px; /* Adjust based on parent padding/header */
  z-index: 100;
  background: rgba(26, 26, 46, 0.9);
  backdrop-filter: blur(10px);
  padding: 15px 0;
  margin: -20px -20px 20px -20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.category-filter {
  display: flex;
  overflow-x: auto;
  gap: 12px;
  padding: 0 20px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.category-filter::-webkit-scrollbar {
  display: none;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 65px;
  height: 65px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #aaa;
}

.category-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.category-item.active {
  background: linear-gradient(135deg, #00d9ff, #00ff88);
  border-color: transparent;
  color: #1a1a2e;
}

/* Time Suggestion Modal Added CSS */
.suggestion-text {
  color: #aaa;
  margin-bottom: 25px;
  line-height: 1.5;
}

.time-slots-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 25px;
}

.btn-time-slot {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 217, 255, 0.3);
  padding: 15px;
  border-radius: 8px;
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-time-slot:hover {
  background: rgba(0, 217, 255, 0.1);
  transform: translateY(-2px);
}

.btn-time-slot.selected {
  background: linear-gradient(135deg, #00d9ff, #00ff88);
  border-color: transparent;
  color: #1a1a2e;
  box-shadow: 0 4px 15px rgba(0, 255, 136, 0.3);
}

@media (max-width: 600px) {
  .time-slots-grid {
    grid-template-columns: 1fr;
  }
}

.cat-icon {
  font-size: 1.2rem;
  margin-bottom: 4px;
}

.cat-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-item.hidden {
  display: none;
}

.btn-secondary {
  flex: 1;
  padding: 12px;
  background: transparent;
  border: 1px solid #666;
  border-radius: 8px;
  color: #aaa;
  font-weight: bold;
  cursor: pointer;
}

.cart-item-info {
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.cart-item-modifications {
  margin-top: 4px;
  line-height: 1.2;
}

.text-success {
  color: #00ff88;
}

.text-danger {
  color: #ff4444;
}

@media (max-width: 600px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .menu-header {
    flex-direction: column;
    gap: 15px;
  }
  
  .dishes-grid {
    grid-template-columns: 1fr;
  }

  .variation-grid {
    grid-template-columns: 1fr;
  }
}
</style>
